<template>
    <form
        class="container-fluid form-container"
        @submit.prevent="saveForm"
    >
        <div class="row">
            <div class="col-sm-12">
                <h1 class="page-title">
                    Fire Training Form
                </h1>
            </div>
        </div>
        <div
            class="card"
            v-if="loading"
        >
            <div class="card-body">
                <p>Loading...</p>
            </div>
        </div>
        <invoice-form
            v-if="!loading"
            :form="form"
        />
        <div
            class="card"
            v-if="!loading"
        >
            <div class="card-body">
                <div class="row">
                    <div class="col-sm-12">
                        <h3>Customer Details</h3>
                    </div>
                </div>
                <div class="row">
                    <div class="col">
                        <label for="customer">Customer</label>
                        <customer-search
                            ref="customerSearch"
                            :customer="form.customer"
                            @change="customerChanged"
                            :customer-id="form.customer_id"
                        />
                        <error
                            name="customer_id"
                            :errors="errors"
                        />
                    </div>
                    <div
                        class="col"
                        v-if="form.customer && form.customer.sites"
                    >
                        <label for="site">Site:</label>
                        <select
                            name="site"
                            v-model="form.site_id"
                            id="site"
                            class="form-control"
                            placeholder="Site"
                            required
                            v-if="form.customer.sites.length"
                        >
                            <option value="">
                                Select Site
                            </option>
                            <option
                                v-for="site in form.customer.sites"
                                :key="site.id"
                                :value="site.id"
                            >
                                #{{ site.site_no }} {{ site.name }}
                            </option>
                        </select>
                        <span
                            class="form-error"
                            v-if="!form.customer.sites.length"
                        >This customer has no sites</span>
                        <error
                            name="site_id"
                            :errors="errors"
                        />
                    </div>
                    <div class="col">
                        <label for="job_id">Job No.</label>
                        <select
                            v-if="jobs"
                            name="job_id"
                            id="job_id"
                            v-model="form.job_id"
                            class="form-control"
                            placeholder="Select a job"
                            :disabled="jobs.length < 1"
                        >
                            <option
                                :value="null"
                                disabled
                            >
                                Select a job
                            </option>
                            <option
                                v-for="job in jobs"
                                :key="job.id"
                                :value="job.id"
                            >
                                {{ job.id }}: {{ job.name }}
                            </option>
                        </select>
                        <error
                            name="job_id"
                            :errors="errors"
                        />
                    </div>
                </div>
                <div class="row">
                    <div class="col">
                        <label class="form-label">Date</label>
                        <date-picker
                            v-model="form.test_date"
                            :options="{format: 'L'}"
                            required
                        />
                        <error
                            name="test_date"
                            :errors="errors"
                        />
                    </div>
                    <div class="col">
                        <label for="training_type">Type of training</label>
                        <select
                            id="training_type"
                            name="training_type"
                            v-model="form.training_type"
                            class="form-control"
                        >
                            <option value="">
                                Select type
                            </option>
                            <option value="fire_marshal">
                                Fire Marshal
                            </option>
                            <option value="basic_fire_safety">
                                Basic Fire Safety
                            </option>
                            <option value="care_homes">
                                Care Homes
                            </option>
                        </select>
                        <error
                            name="training_type"
                            :errors="errors"
                        />
                    </div>
                    <div class="col">
                        <label>Trainer</label>
                        <user-select
                            v-model="form.engineer_id"
                            :office="true"
                            :admins="true"
                        />
                        <error
                            name="engineer_id"
                            :errors="errors"
                        />
                    </div>
                </div>
            </div>
        </div>
        <div
            class="card"
            v-if="!loading"
        >
            <div class="card-body">
                <div class="row">
                    <div class="col">
                        <h3>Attendees</h3>
                    </div>
                </div>
                <div class="row">
                    <div class="col">
                        <table class="table">
                            <tbody>
                                <tr
                                    v-for="(attendee, index) in form.attendees"
                                    :key="'attendee_row_'+index"
                                >
                                    <td>
                                        <input
                                            type="text"
                                            v-model="attendee.name"
                                            class="form-control"
                                            placeholder="Full Name"
                                            :ref="'attendee_'+index"
                                        >
                                    </td>
                                    <td class="text-right text-nowrap">
                                        <span
                                            v-if="attendee.certificate_no"
                                            class="mr-2"
                                        >{{ attendee.certificate_no }}</span>
                                        <button
                                            type="button"
                                            class="btn btn-sm btn-outline-primary"
                                            @click="generateCertificate(attendee)"
                                            :disabled="saving"
                                        >
                                            Generate Certificate
                                        </button>
                                        <button
                                            type="button"
                                            class="btn btn-sm btn-outline-danger"
                                            @click="removeAttendee(index)"
                                            v-show="form.attendees.length > 1"
                                        >
                                            <i class="fa fa-times" />
                                        </button>
                                    </td>
                                </tr>
                            </tbody>
                            <tfoot>
                                <tr>
                                    <td
                                        class="text-right"
                                        colspan="2"
                                    >
                                        <span>{{ form.attendees.length }} Attendee(s)</span>
                                        <button
                                            class="btn btn-primary"
                                            type="button"
                                            @click="addAttendee()"
                                        >
                                            <i class="fa fa-plus" />
                                            Add Attendee
                                        </button>
                                    </td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>
            </div>
        </div>
        <div
            class="card"
            v-if="!loading"
        >
            <div class="card-body">
                <div class="row">
                    <div class="col">
                        <button
                            v-if="!saving"
                            class="btn btn-primary"
                        >
                            Save
                        </button>
                        <button
                            v-if="saving"
                            class="btn btn-disabled"
                            disabled
                        >
                            Saving...
                        </button>
                        <button
                            class="btn btn-outline-danger"
                            type="button"
                            @click="deleteForm()"
                            :disabled="saving"
                            v-if="form.id && canDelete"
                        >
                            <i class="fa fa-times" /> Delete
                        </button>
                        <a
                            class="btn btn-info"
                            v-if="form.id"
                            :href="'/api/forms/' + form.id + '/stream'"
                        >
                            <i class="fa fa-print" /> Preview/Print
                        </a>

                        <a
                            class="btn btn-info"
                            v-if="form.id"
                            :href="'/api/forms/' + form.id + '/download'"
                            target="_blank"
                            :download="form.doc_no + '.pdf'"
                        >
                            <i class="fa fa-file" /> Download PDF
                        </a>

                        <customer-site-footer-links
                            :customer-id="form.customer_id"
                            :site-id="form.site_id"
                        />

                        <activity-log
                            v-if="form.id"
                            :id="form.id"
                            model="Form"
                        />
                    </div>
                </div>
            </div>
        </div>
    </form>
</template>

<script>
import DatePicker from "../DateTimePickerComponent.vue";
import UserSelect from "../UserSelectComponent.vue";
import CustomerSearch from "../CustomerSearch.vue";
import JobService from "../../services/JobService"
import FormService from "../../services/FormService"
import AuthService from "../../services/AuthService"
import UsesErrors from "../../mixins/UsesErrors";
import UsesInfoMessage from "../../mixins/UsesInfoMessage";
import UsesFormRouteQuery from "../../mixins/UsesFormRouteQuery";
import moment from 'moment';
import InvoiceForm from "./parts/InvoiceFormComponent.vue";

function newAttendeeId() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
        return window.crypto.randomUUID();
    }
    return 'attendee-' + Date.now() + '-' + Math.random().toString(16).slice(2);
}

function blankAttendee() {
    return {
        id: newAttendeeId(),
        name: '',
    };
}

const initialForm = {
    test_date: window.moment(),
    engineer_id: null,
    training_type: '',
    attendees: [],
};

export default {
    components: {
        InvoiceForm,
        DatePicker,
        UserSelect,
        CustomerSearch,
    },
    mixins: [
        UsesErrors,
        UsesInfoMessage,
        UsesFormRouteQuery,
    ],
    data() {
        return {
            form: {...initialForm},
            jobs: [],
            saving: false,
            loading: false,
            token: null,
        };
    },
    computed: {
        canDelete() {
            return AuthService.user.admin_access || AuthService.user.office_access;
        },
    },
    watch: {
        '$route': {
            deep: true,
            immediate: true,
            handler() {
                this.loading = true;
                if (this.$route.params.id) {
                    FormService.get(this.$route.params.id).then(({data}) => {
                        this.setFormFromApi(data);
                        this.loading = false;
                    }).catch(() => {
                        window.alert('Error: Failed to load form');
                        this.$route.back();
                    });
                    return
                }

                this.form = {
                    ...initialForm,
                    test_date: window.moment(),
                    attendees: [blankAttendee()],
                };
                FormService.nextNo().then(res => {
                    this.form.doc_no = res.data;
                });
                this.initialiseFromQueryParams();
                this.loading = false;
            }
        }
    },
    mounted() {
    },
    methods: {
        initialiseFromQueryParams() {
            this.populateFormFromRouteQuery();
        },
        onCustomerIdSetFromRoute() {
            this.customerChanged();
        },

        onJobLoadedFromRoute(jobData) {
            const timeSlot = jobData.time_slots && jobData.time_slots[0];
            if (timeSlot && timeSlot.start) {
                this.form.test_date = moment(timeSlot.start);
            }
            this.getJobs();
        },

        removeAttendee(index) {
            this.form.attendees.splice(index, 1);
        },
        addAttendee() {
            this.form.attendees.push(blankAttendee());
            window.setTimeout(() => {
                const lastRef = 'attendee_' + (this.form.attendees.length - 1);
                this.$refs[lastRef][0].focus();
            }, 200);
        },
        customerChanged(customer) {
            this.form.customer = customer ? customer : {};
            if (this.form.customer) {
                this.form.customer_id = this.form.customer.id;
            } else {
                this.form.customer_id = null;
            }
            if (customer && customer.sites) {
                if (!customer.sites.map(site => site.id).includes(this.form.site_id)) {
                    this.form.site_id = '';
                }
            } else {
                this.form.site_id = '';
            }
            this.getJobs();
        },
        getJobs() {
            if (!this.form.customer_id) {
                this.jobs = [];
                this.form.job_id = null;
                return;
            }
            JobService.search({
                "filter[customer_id]": this.form.customer_id
            }).then(
                res => {
                    this.jobs = res.data.data || [];
                },
                err => {
                    this.setErrorMessage("Could not load customer jobs");
                    this.jobs = [];
                }
            );
        },
        formPayload() {
            let formData = {...this.form, type: 'fire_training'};
            delete formData.customer;
            delete formData.site;
            delete formData.job;
            delete formData.data;
            return formData;
        },
        goToEdit(id) {
            if (this.$route.path.includes("engineer/")) {
                this.$router.push({
                    name: "engineer.forms.edit",
                    params: {
                        id: id,
                    },
                });
                return;
            }
            this.$router.push({
                name: "office.forms.edit",
                params: {
                    id: id,
                },
            });
        },
        saveForm() {
            this.saving = true;
            FormService.save(this.formPayload()).then(({data}) => {
                this.setInfoMessage("Form saved successfully");
                if (!this.form.id) {
                    this.goToEdit(data.id);
                } else {
                    this.setFormFromApi(data);
                    this.saving = false;
                }
            }).catch(err => {
                this.saving = false;
                this.captureRequestErrors(err);
            });
        },
        generateCertificate(attendee) {
            const problems = {};
            if (!attendee.name || !String(attendee.name).trim()) {
                problems.name = ['Enter the attendee name before generating a certificate.'];
            }
            if (!this.form.training_type) {
                problems.training_type = ['Choose a type of training before generating a certificate.'];
            }
            if (!this.form.test_date) {
                problems.test_date = ['Enter the training date before generating a certificate.'];
            }
            if (!this.form.engineer_id) {
                problems.engineer_id = ['Choose a trainer before generating a certificate.'];
            }
            if (Object.keys(problems).length) {
                const firstKey = Object.keys(problems)[0];
                this.setErrorMessage(problems[firstKey][0]);
                this.updateErrors(problems);
                return;
            }
            if (!attendee.id) {
                this.$set(attendee, 'id', newAttendeeId());
            }
            const preview = window.open('', '_blank');
            this.saving = true;
            const attendeeId = attendee.id;
            const wasNew = !this.form.id;
            FormService.save(this.formPayload()).then(({data}) => {
                const formId = data.id;
                const url = '/api/forms/' + formId + '/attendees/' + encodeURIComponent(attendeeId) + '/certificate';
                if (preview && !preview.closed) {
                    preview.location.href = url;
                } else {
                    window.open(url, '_blank');
                }
                if (wasNew) {
                    this.saving = false;
                    this.goToEdit(formId);
                    return;
                }
                this.refreshCertificateForm(formId, attendeeId);
            }).catch(err => {
                this.saving = false;
                if (preview && !preview.closed) {
                    preview.close();
                }
                this.captureRequestErrors(err);
            });
        },
        refreshCertificateForm(formId, attendeeId) {
            const started = Date.now();
            const attempt = () => {
                FormService.get(formId).then(({data: fresh}) => {
                    let numbered = false;
                    if (fresh.attendees) {
                        fresh.attendees.forEach(row => {
                            if (row.id === attendeeId && row.certificate_no) {
                                numbered = true;
                            }
                        });
                    }
                    if (numbered || Date.now() - started > 20000) {
                        this.setInfoMessage("Form saved successfully");
                        this.setFormFromApi(fresh);
                        this.saving = false;
                        return;
                    }
                    setTimeout(attempt, 400);
                }).catch(err => {
                    this.saving = false;
                    this.captureRequestErrors(err);
                });
            };
            attempt();
        },
        setFormFromApi(data) {
            let attendees = [blankAttendee()];
            if (data.attendees && data.attendees.length) {
                attendees = data.attendees.map(attendee => {
                    if (attendee && attendee.id) {
                        return attendee;
                    }
                    return Object.assign({}, attendee || {}, { id: newAttendeeId() });
                });
            }
            this.form = {
                ...data,
                test_date: window.moment(data.test_date),
                training_type: data.training_type || '',
                attendees,
            };
        },
        deleteForm() {
            if (confirm("Are you sure you want to delete this form?")) {
                FormService.delete(this.form).then(
                    () => {
                        this.setInfoMessage("Form successfully deleted");
                        this.$router.push({name: 'office.forms.search'});
                    },
                    error => {
                        this.setErrorMessage("Could not delete this form");
                        console.error(error);
                    }
                )
            }
        },
    },
}
</script>


// WEBPACK FOOTER //
// resources/assets/js/components/forms/FireTrainingForm.vue